import { useEffect, useState, type FormEvent } from "react";
import { Bell, CheckCircle2, Loader2, MapPin } from "lucide-react";
import type { OnboardingPreview } from "../../services/OnboardingPreviewService";
import { useTranslation } from "../../context/LanguageContext";
import { dealsFromPreview, previewPlace, type NearestStore } from "../../utils/onboardingCoverage";
import { lookupZipPlace } from "../../utils/geocoding";
import { C, DealGridCard, DealGridSkeleton, OnbPage, OnbTopBar, YellowCta } from "./DealsFirstParts";
import { useIsDesktop } from "./PlansStepScreen";

interface Props {
  zip: string;
  examples: OnboardingPreview | null | undefined;
  /** Closest Walmart/HD stores at any distance; the card hides when empty. */
  nearest: NearestStore[];
  /** "Choose a plan anyway" needs a store the store step can list. */
  canChoosePlan: boolean;
  defaultEmail: string;
  onNotify: (email: string) => Promise<void>;
  onBrowseFree: () => void;
  onChoosePlan: () => void;
  onTryZip: () => void;
  onSignOut: () => void;
}

type NotifyState = "idle" | "saving" | "done" | "error";

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function NotifyCard({ zip, defaultEmail, onNotify, compact }: { zip: string; defaultEmail: string; onNotify: Props["onNotify"]; compact: boolean }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState(defaultEmail);
  const [state, setState] = useState<NotifyState>("idle");
  const [invalid, setInvalid] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const value = email.trim();
    if (!EMAIL.test(value)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    setState("saving");
    try {
      await onNotify(value);
      setState("done");
    } catch {
      setState("error");
    }
  };

  return (
    <div className={`flex flex-col gap-3 rounded-[20px] border ${compact ? "p-4" : "p-5"}`} style={{ backgroundColor: C.card, borderColor: C.line }}>
      <span className="flex items-center gap-2.5">
        <span className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-xl" style={{ backgroundColor: C.iconBg, color: C.icon }}>
          {state === "done" ? <CheckCircle2 size={19} /> : <Bell size={19} />}
        </span>
        <b className={compact ? "text-[15.5px]" : "text-[17px]"}>{state === "done" ? t("onboarding2.nc_notify_done") : t("onboarding2.nc_notify_title")}</b>
      </span>
      {state === "done" ? (
        <p className="m-0 text-[13.5px] leading-[19px]" style={{ color: C.muted }} role="status">
          {t("onboarding2.nc_notify_done_body", { email: email.trim(), zip })}
        </p>
      ) : (
        <>
          <p className="m-0 text-[13.5px] leading-[19px]" style={{ color: C.muted }}>{t("onboarding2.nc_notify_body")}</p>
          <form onSubmit={(e) => void submit(e)} noValidate className="flex gap-2">
            <input
              type="email"
              autoComplete="email"
              aria-label={t("onboarding2.nc_email_label")}
              aria-invalid={invalid || undefined}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setInvalid(false);
                if (state === "error") setState("idle");
              }}
              className="h-12 min-w-0 flex-1 rounded-[13px] border-[1.5px] px-3.5 text-[15px] outline-none transition focus:border-[#7C3AED] focus:ring-4 focus:ring-[#7C3AED]/15"
              style={{ backgroundColor: C.field, borderColor: invalid ? "#E5322D" : C.fieldBorder, color: C.ink }}
            />
            <button
              type="submit"
              disabled={state === "saving"}
              className="flex h-12 flex-none items-center justify-center rounded-[13px] px-[18px] text-[14.5px] font-extrabold transition hover:opacity-90 disabled:opacity-70"
              style={{ backgroundColor: C.inkBtn, color: C.onInkBtn }}
            >
              {state === "saving" ? <Loader2 className="h-4 w-4 animate-spin" aria-label={t("onboarding2.nc_notify_cta")} /> : t("onboarding2.nc_notify_cta")}
            </button>
          </form>
          {invalid ? <p role="alert" className="m-0 text-[12.5px] font-semibold" style={{ color: C.errorInk }}>{t("onboarding2.nc_email_invalid")}</p> : null}
          {state === "error" ? <p role="alert" className="m-0 text-[12.5px] font-semibold" style={{ color: C.errorInk }}>{t("onboarding2.nc_notify_error")}</p> : null}
        </>
      )}
    </div>
  );
}

function NearestCard({ nearest }: { nearest: NearestStore[] }) {
  const { t } = useTranslation();
  // Most Walmart catalog rows carry only a street and a ZIP: look the city up.
  const [places, setPlaces] = useState<Record<string, string>>({});
  const missing = nearest.filter((store) => !store.place && store.postalCode).map((store) => store.postalCode).join(",");
  useEffect(() => {
    if (!missing) return;
    let cancelled = false;
    for (const zip of missing.split(",")) {
      void lookupZipPlace(zip).then((found) => {
        if (!cancelled && found) setPlaces((current) => ({ ...current, [zip]: found }));
      });
    }
    return () => {
      cancelled = true;
    };
  }, [missing]);
  return (
    <div className="rounded-[20px] border px-5 pb-2 pt-[18px]" style={{ backgroundColor: C.card, borderColor: C.line }}>
      <span className="block pb-1.5 text-[12px] font-extrabold uppercase tracking-[0.8px]" style={{ color: C.soft }}>{t("onboarding2.nc_closest")}</span>
      <ul className="m-0 list-none p-0">
        {nearest.map((store) => (
          <li key={`${store.retailer}-${store.storeId}`} className="flex items-center gap-3 border-t py-3" style={{ borderColor: C.line }}>
            <span className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-[10px]" style={{ backgroundColor: C.chip, color: C.muted }}>
              <MapPin size={17} />
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <b className="truncate text-[14.5px]">
                {t(store.retailer === "walmart" ? "onboarding2.nc_store_walmart" : "onboarding2.nc_store_home_depot", { id: store.storeId })}
              </b>
              {store.place || places[store.postalCode] ? (
                <span className="truncate text-[12.5px]" style={{ color: C.muted }}>{store.place || places[store.postalCode]}</span>
              ) : null}
            </span>
            <b className="flex-none text-[14px]" style={{ color: C.body }}>{t("onboarding2.miles", { miles: Math.round(store.miles) })}</b>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Step 2 when no Walmart or HD is near the ZIP: honest copy, real example deals
 * from the example ZIP, a "notify me" capture, the closest stores, and the free
 * online feed as the main way forward.
 */
export default function NotCoveredScreen(props: Props) {
  const { t } = useTranslation();
  const desktop = useIsDesktop();
  const place = previewPlace(props.examples);
  const deals = props.examples === undefined ? undefined : dealsFromPreview(props.examples, place ? t("onboarding2.dp_example_store", { place }) : "").slice(0, desktop ? 4 : 2);
  const sub = place
    ? t(desktop ? "onboarding2.nc_sub" : "onboarding2.nc_sub_short", { place })
    : t("onboarding2.nc_sub_generic");

  const header = (
    <div>
      <span className="text-[12px] font-extrabold uppercase tracking-[1px] lg:text-[13px]" style={{ color: C.soft }}>
        {t("onboarding2.near", { zip: props.zip })}
      </span>
      <h1 className="m-0 mt-[3px] text-[26px] font-black leading-[29px] tracking-[-0.8px] lg:mt-1 lg:text-[34px] lg:leading-[38px] lg:tracking-[-1px]">
        {t("onboarding2.nc_title")}
      </h1>
      <p className="m-0 mt-1.5 max-w-[620px] text-[14px] leading-5 lg:mt-2 lg:text-[16px] lg:leading-[23px]" style={{ color: C.muted }}>{sub}</p>
    </div>
  );

  const examples = deals === undefined ? (
    <DealGridSkeleton count={desktop ? 4 : 2} columns="grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-[18px]" />
  ) : deals.length > 0 ? (
    <>
      <div className="flex items-center gap-2">
        <span className="flex h-[22px] items-center rounded-md px-2 text-[10.5px] font-extrabold uppercase tracking-[0.5px] lg:h-6 lg:rounded-[7px] lg:px-[9px] lg:text-[11.5px]" style={{ backgroundColor: C.chip, color: C.muted }}>
          {t("onboarding2.dp_examples")}
        </span>
        <b className="text-[14px] lg:text-[16px]">{place ? t("onboarding2.dp_examples_from", { place }) : t("onboarding2.dp_examples_nationwide")}</b>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-[18px]">
        {deals.map((deal) => <DealGridCard key={deal.key} deal={deal} />)}
      </div>
    </>
  ) : null;

  const planLink = props.canChoosePlan ? (
    <button type="button" onClick={props.onChoosePlan} className="font-bold underline underline-offset-2" style={{ color: C.ink }}>
      {t("onboarding2.nc_plan_anyway")}
    </button>
  ) : null;
  const zipLink = (
    <button type="button" onClick={props.onTryZip} className="underline underline-offset-2" style={{ color: C.muted }}>
      {t("onboarding2.nc_try_zip")}
    </button>
  );
  const notify = <NotifyCard zip={props.zip} defaultEmail={props.defaultEmail} onNotify={props.onNotify} compact={!desktop} />;
  const nearest = props.nearest.length > 0 ? <NearestCard nearest={props.nearest} /> : null;

  if (desktop) {
    return (
      <OnbPage testId="onboarding-not-covered">
        <OnbTopBar onSignOut={props.onSignOut} />
        <main className="mx-auto flex w-full max-w-[1280px] items-start gap-10 px-10 pb-16 pt-10">
          <section className="onb-rise flex min-w-0 max-w-[780px] flex-1 flex-col gap-5">
            {header}
            {examples}
          </section>
          <aside className="onb-rise flex w-[380px] flex-none flex-col gap-4" style={{ animationDelay: "60ms" }}>
            {notify}
            {nearest}
            <YellowCta height={54} fontSize={17} onClick={props.onBrowseFree}>{t("onboarding2.nc_browse_free")}</YellowCta>
            {planLink ? (
              <p className="m-0 text-center text-[13.5px]" style={{ color: C.muted }}>
                {props.nearest.length > 0 ? `${t("onboarding2.nc_plan_prompt")} ` : null}
                {planLink}
              </p>
            ) : null}
            <p className="m-0 text-center text-[13.5px]">{zipLink}</p>
          </aside>
        </main>
      </OnbPage>
    );
  }

  return (
    <OnbPage testId="onboarding-not-covered">
      <OnbTopBar onSignOut={props.onSignOut} />
      <main className="onb-rise mx-auto flex w-full max-w-[640px] flex-col gap-3.5 px-[18px] pb-[150px] pt-6">
        {header}
        {examples}
        {notify}
        {nearest}
      </main>
      <div
        className="fixed inset-x-0 bottom-0 z-30 mx-auto flex w-full max-w-[640px] flex-col gap-2.5 px-[18px] pb-[max(env(safe-area-inset-bottom),22px)] pt-6"
        style={{ background: `linear-gradient(to bottom, transparent, ${C.page} 30%)` }}
      >
        <YellowCta height={54} fontSize={17} onClick={props.onBrowseFree}>{t("onboarding2.nc_browse_free")}</YellowCta>
        <p className="m-0 text-center text-[13px]" style={{ color: C.muted }}>
          {zipLink}
          {planLink ? <> · {planLink}</> : null}
        </p>
      </div>
    </OnbPage>
  );
}
